import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

/**
 * Portfolio Context - Persists portfolio data across route navigation
 * 
 * This context stores:
 * - portfolioItems: Raw uploaded positions
 * - analysisResult: Full API response with scores, tests, etc.
 * - scores: Transformed score data for table display
 * - recommendations: Top recommendations
 */

const PortfolioContext = createContext(null);

export function PortfolioProvider({ children }) {
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [scores, setScores] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [lastAnalyzed, setLastAnalyzed] = useState(null);

  // Track whether initial mount restore has run (prevents overwriting localStorage on mount)
  const hasRestoredRef = useRef(false);

  // Restore from localStorage on mount
  useEffect(() => {
    if (hasRestoredRef.current) return;
    hasRestoredRef.current = true;
    try {
      const savedItems = localStorage.getItem('alphagrade_portfolio_items');
      const savedResult = localStorage.getItem('alphagrade_analysis_result');
      if (savedItems) {
        const items = JSON.parse(savedItems);
        if (Array.isArray(items) && items.length > 0) {
          setPortfolioItems(items);
        }
      }
      if (savedResult) {
        const result = JSON.parse(savedResult);
        if (result && result.items) {
          // Re-run storeAnalysisResult logic to hydrate scores/recommendations
          setAnalysisResult(result);
          setLastAnalyzed(result._savedAt ? new Date(result._savedAt) : null);
          const responseItems = result.items || [];
          const transformedScores = responseItems.map(h => ({
            symbol: h.symbol,
            stock_quality_score: h.scores?.quality_score ?? null,
            stock_setup_score: h.scores?.setup_score ?? null,
            convex_radar_score: h.convex_radar?.score_0to5 ?? null,
            energy_matrix_score: h.energy?.score_0to5 ?? null,
            combined_conviction: h.conviction?.combined_conviction ?? null,
            short_term_conviction: h.conviction?.short_term_conviction ?? h.conviction?.combined_conviction ?? null,
            long_term_conviction: h.conviction?.long_term_conviction ?? h.conviction?.combined_conviction ?? null,
            recommended_action: h.conviction?.recommended_action ?? null,
            short_term_action: h.conviction?.short_term_action ?? h.conviction?.recommended_action ?? null,
            long_term_action: h.conviction?.long_term_action ?? h.conviction?.recommended_action ?? null,
            conviction_band: h.conviction?.conviction_band ?? null,
            guidance: h.conviction?.guidance ?? null,
            weights_used: h.conviction?.weights_used ?? null,
            current_price: h.current_price,
            pnl_percent: h.pnl_percent,
            position_value: h.position_value,
            is_crypto: h.data_quality?.is_crypto ?? false,
            quality_pillars: h.scores?.quality_pillars || {},
            setup_pillars: h.scores?.setup_pillars || {},
            convex_tests: h.convex_radar?.tests || {},
            data_quality: h.data_quality || {},
          }));
          setScores(transformedScores);
          const recs = responseItems
            .filter(h => h.conviction)
            .map(h => ({
              symbol: h.symbol,
              recommendation: h.conviction?.recommended_action,
              total_score: h.conviction?.combined_conviction,
              conviction_band: h.conviction?.conviction_band,
              stock_quality_score: h.scores?.quality_score,
              stock_setup_score: h.scores?.setup_score,
              convex_radar_score: h.convex_radar?.score_0to5,
              energy_matrix_score: h.energy?.score_0to5,
              pnl_percent: h.pnl_percent,
            }))
            .sort((a, b) => (b.total_score ?? 0) - (a.total_score ?? 0));
          setRecommendations(recs);
        }
      }
    } catch (e) {
      console.error('Failed to restore portfolio from localStorage:', e);
    }
  }, []);

  // Persist to localStorage when analysis changes
  useEffect(() => {
    if (!hasRestoredRef.current) return;
    if (portfolioItems.length > 0) {
      localStorage.setItem('alphagrade_portfolio_items', JSON.stringify(portfolioItems));
    }
    if (analysisResult) {
      localStorage.setItem('alphagrade_analysis_result', JSON.stringify({
        ...analysisResult,
        _savedAt: new Date().toISOString(),
      }));
    }
  }, [portfolioItems, analysisResult]);

  // Clear all data
  const clearPortfolio = useCallback(() => {
    setPortfolioItems([]);
    setAnalysisResult(null);
    setScores([]);
    setRecommendations([]);
    setLastAnalyzed(null);
    localStorage.removeItem('alphagrade_portfolio_items');
    localStorage.removeItem('alphagrade_analysis_result');
  }, []);

  // Store analysis results
  const storeAnalysisResult = useCallback((result, items) => {
    setAnalysisResult(result);
    setLastAnalyzed(new Date());
    
    // Transform snapshot response to scores format
    const responseItems = result.items || [];
    
    const transformedScores = responseItems.map(h => ({
      symbol: h.symbol,
      stock_quality_score: h.scores?.quality_score ?? null,
      stock_setup_score: h.scores?.setup_score ?? null,
      convex_radar_score: h.convex_radar?.score_0to5 ?? null,
      energy_matrix_score: h.energy?.score_0to5 ?? null,
      combined_conviction: h.conviction?.combined_conviction ?? null,
      short_term_conviction: h.conviction?.short_term_conviction ?? h.conviction?.combined_conviction ?? null,
      long_term_conviction: h.conviction?.long_term_conviction ?? h.conviction?.combined_conviction ?? null,
      recommended_action: h.conviction?.recommended_action ?? null,
      short_term_action: h.conviction?.short_term_action ?? h.conviction?.recommended_action ?? null,
      long_term_action: h.conviction?.long_term_action ?? h.conviction?.recommended_action ?? null,
      conviction_band: h.conviction?.conviction_band ?? null,
      guidance: h.conviction?.guidance ?? null,
      weights_used: h.conviction?.weights_used ?? null,
      current_price: h.current_price,
      pnl_percent: h.pnl_percent,
      position_value: h.position_value,
      is_crypto: h.data_quality?.is_crypto ?? false,
      // Include full test breakdown for detail view
      quality_pillars: h.scores?.quality_pillars || {},
      setup_pillars: h.scores?.setup_pillars || {},
      convex_tests: h.convex_radar?.tests || {},
      data_quality: h.data_quality || {},
    }));
    
    setScores(transformedScores);
    
    // Update portfolio items with current prices
    const updatedItems = items.map(item => {
      const holding = responseItems.find(h => h.symbol === item.symbol);
      return {
        ...item,
        current_price: holding?.current_price ?? item.entry_price
      };
    });
    setPortfolioItems(updatedItems);
    
    // Transform to recommendations
    const recs = responseItems
      .filter(h => h.conviction)
      .map(h => ({
        symbol: h.symbol,
        recommendation: h.conviction?.recommended_action,
        total_score: h.conviction?.combined_conviction,
        conviction_band: h.conviction?.conviction_band,
        stock_quality_score: h.scores?.quality_score,
        stock_setup_score: h.scores?.setup_score,
        convex_radar_score: h.convex_radar?.score_0to5,
        energy_matrix_score: h.energy?.score_0to5,
        pnl_percent: h.pnl_percent,
      }))
      .sort((a, b) => (b.total_score ?? 0) - (a.total_score ?? 0));
    // No longer limiting to 10 - show ALL holdings
    
    setRecommendations(recs);
  }, []);

  // Append a single score (for progressive loading as each position completes)
  const appendScore = useCallback((newScore) => {
    setScores(prev => {
      const exists = prev.find(s => s.symbol === newScore.symbol);
      if (exists) {
        return prev.map(s => s.symbol === newScore.symbol ? { ...s, ...newScore } : s);
      }
      return [...prev, newScore];
    });
  }, []);

  const value = {
    portfolioItems,
    setPortfolioItems,
    analysisResult,
    setAnalysisResult,
    scores,
    setScores,
    recommendations,
    setRecommendations,
    loading,
    setLoading,
    progress,
    setProgress,
    lastAnalyzed,
    clearPortfolio,
    storeAnalysisResult,
    appendScore,
  };

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
}

export function usePortfolio() {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
}

export default PortfolioContext;
